import React, { useEffect, useState } from "react";

/**
 * Side panels in a stack, opening from the right, one in front of the
 * other — the "double sidebar", for any number of levels. The two frontmost
 * open panels show in full, side by side; every panel behind them collapses
 * to a sliver (`peek` px) at the left, hazed, with the multilayer lightbox's
 * hover back-arrow — click it and everything in front of it closes. Owns
 * everything the panels need to agree on, which every earlier double
 * sidebar wired by hand:
 *
 *   - each panel's offset from the right, from the widths of the panels in
 *     front of it (and the sliver rule for covered ones);
 *   - only the frontmost open panel closes on an outside click;
 *   - later panels paint above earlier ones;
 *   - closing a panel closes every panel in front of it (a level cannot
 *     outlive the one it opened from).
 *
 * Each level is any component that renders a SidePanel (or TagDetailSidebar)
 * and takes the shell props — pass it as a render function of them:
 *
 *   <SidePanelStack
 *     levels={[
 *       { key: "topic", isOpen, width: 440, onClose, render: (shell) => <SidePanel {...shell} … /> },
 *       { key: "row",   isOpen, width: 440, onClose, render: (shell) => <TagDetailSidebar {...shell} … /> },
 *     ]}
 *   />
 *
 * `shell` is { rightOffset, zIndex, closeOnOutsideClick, onClose, covered,
 * onUncover, peekWidth }. The `onClose` handed to a level wraps yours with
 * the cascade. Levels are given in opening order (first opens first).
 */
const BASE_Z = 1000;
const DEFAULT_PEEK = 160;

const useViewportWidth = () => {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 0 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
};

export const SidePanelStack = ({ levels, peek = DEFAULT_PEEK }) => {
  const viewport = useViewportWidth();
  const open = levels.filter((l) => l?.isOpen);
  const n = open.length;

  // Left edges, computed front to back (each one from the panel in front
  // of it): the front panel flush right, the one behind it beside it in
  // full, and every earlier one `peek` px further left than the panel in
  // front of it (so only that much of it shows).
  const leftOf = new Map();
  for (let i = n - 1; i >= 0; i -= 1) {
    const level = open[i];
    const fromFront = n - 1 - i;
    if (fromFront === 0) leftOf.set(level, viewport - level.width);
    else if (fromFront === 1) leftOf.set(level, leftOf.get(open[i + 1]) - level.width);
    else leftOf.set(level, leftOf.get(open[i + 1]) - peek);
  }

  // Close this level and everything in front of it, front first.
  const closeFrom = (index) => {
    for (let j = open.length - 1; j >= index; j -= 1) open[j].onClose?.();
  };

  return (
    <>
      {levels.map((level, z) => {
        if (!level) return null;
        const i = open.indexOf(level);
        const isOpen = i >= 0;
        const isFront = isOpen && i === n - 1;
        const covered = isOpen && n - 1 - i >= 2;
        const rightOffset = isOpen ? viewport - leftOf.get(level) - level.width : 0;
        return (
          <React.Fragment key={level.key ?? z}>
            {level.render({
              rightOffset: Math.max(0, Math.round(rightOffset)),
              zIndex: BASE_Z + z,
              closeOnOutsideClick: isFront,
              onClose: () => (isOpen ? closeFrom(i) : level.onClose?.()),
              covered,
              onUncover: () => closeFrom(i + 1),
              peekWidth: peek,
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};
